import { Loader2, Plus, X } from "lucide-react";
import { useState } from "react";
import type { OAuthClientCreate } from "@/types";

const EMPTY_FORM: OAuthClientCreate = {
  clientName: "",
  clientType: "public",
  redirectUris: [],
  allowedScopes: ["openid", "profile", "email"],
  grantTypes: ["authorization_code", "refresh_token"],
  requirePkce: true,
};

interface CreateOAuthClientModalProps {
  saving: boolean;
  onClose: () => void;
  /** Validation errors are surfaced via toast by the caller. */
  onValidationError: (message: string) => void;
  onCreate: (payload: OAuthClientCreate) => void;
}

export function CreateOAuthClientModal({
  saving,
  onClose,
  onValidationError,
  onCreate,
}: Readonly<CreateOAuthClientModalProps>) {
  const [form, setForm] = useState<OAuthClientCreate>(EMPTY_FORM);
  const [redirectText, setRedirectText] = useState("");

  const handleSubmit = () => {
    const redirectUris = redirectText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!form.clientName.trim()) {
      onValidationError("Client name is required");
      return;
    }
    if (redirectUris.length === 0) {
      onValidationError("At least one redirect URI is required");
      return;
    }
    onCreate({ ...form, redirectUris });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-surface-secondary border border-border-primary p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-content-primary">New OAuth Client</h3>
          <button type="button" onClick={onClose}>
            <X className="h-5 w-5 text-content-tertiary" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="oc-name" className="block text-xs font-medium text-content-tertiary mb-1">
              Name
            </label>
            <input
              id="oc-name"
              value={form.clientName}
              onChange={(e) => setForm({ ...form, clientName: e.target.value })}
              className="w-full rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-primary"
              placeholder="My External App"
            />
          </div>
          <div>
            <label htmlFor="oc-type" className="block text-xs font-medium text-content-tertiary mb-1">
              Type
            </label>
            <select
              id="oc-type"
              value={form.clientType}
              onChange={(e) => setForm({ ...form, clientType: e.target.value as "public" | "confidential" })}
              className="w-full rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-primary"
            >
              <option value="public">Public (SPA / native — PKCE required)</option>
              <option value="confidential">Confidential (server — gets a secret)</option>
            </select>
          </div>
          <div>
            <label htmlFor="oc-redirects" className="block text-xs font-medium text-content-tertiary mb-1">
              Redirect URIs (one per line, exact match)
            </label>
            <textarea
              id="oc-redirects"
              value={redirectText}
              onChange={(e) => setRedirectText(e.target.value)}
              rows={3}
              className="w-full rounded-md bg-surface-primary border border-border-primary px-3 py-2 text-sm text-content-primary font-mono no-ligatures"
              placeholder={"https://app.example.com/auth/callback"}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md text-sm text-content-tertiary hover:text-content-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-brand-primary text-white text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
